/**
 * Building the MSAL client from what the backend says, never from a constant.
 *
 * Every value here comes from `GET /auth/config` (phase 4 doc, section 5), so
 * the bundle carries no tenant, client id or scope of its own and the same
 * build serves a dev station and the production host. The redirect URI is
 * the page's own origin, which is why both `http://localhost:5173` and
 * `https://cashforms.cubanbread.com` are registered (H-9) and why opening the
 * app on any other origin, `127.0.0.1` included, is refused by Entra rather
 * than by this code.
 */
import { LogLevel, PublicClientApplication } from "@azure/msal-browser";

import type { AuthConfigOut } from "../api/public";

export function canInitialiseMsal(config: AuthConfigOut): boolean {
  return (
    config.auth_mode === "entra" &&
    Boolean(config.client_id) &&
    Boolean(config.authority) &&
    config.scopes.length > 0
  );
}

export async function createMsalClient(config: AuthConfigOut): Promise<PublicClientApplication> {
  if (!canInitialiseMsal(config)) {
    throw new Error("/auth/config carries no client id, authority or scope; MSAL cannot start");
  }

  const client = new PublicClientApplication({
    auth: {
      clientId: config.client_id!,
      authority: config.authority!,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
    },
    cache: {
      // sessionStorage, not localStorage: tokens for cash reconciliation data
      // die with the tab rather than persisting across browser sessions on a
      // shared store machine.
      cacheLocation: "sessionStorage",
    },
    system: {
      loggerOptions: {
        logLevel: import.meta.env.DEV ? LogLevel.Info : LogLevel.Warning,
        piiLoggingEnabled: false,
        loggerCallback: (level, message) => {
          if (level <= LogLevel.Warning) console.warn(`[msal] ${message}`);
          else if (import.meta.env.DEV) console.info(`[msal] ${message}`);
        },
      },
    },
  });

  await client.initialize();
  return client;
}
